import { useGlobalNotification } from "@expert/common-ui";
import SendCircleIcon from "@expert/common-ui/assets/SendCircleIcon";
import { getLogger } from "@expert/logging";
import { ActionIcon, type ActionIconProps, Textarea } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { useMutation } from "@tanstack/react-query";
import { type KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { useAgentSdk, useChatStore } from "@/sdk";
import type { ChatTask } from "@/sdk/agent/chat";
import { type ScrollAreaViewport, scrollToBottom } from "../ChatMessages/scrollUtils";
import classes from "./message-input-styles.module.css";

const logger = getLogger({ module: "MessageInput" });

const containsFourDigits = (input: string) => /(?:\d\D*){4,}/.test(input);

function SendButton({ onClick, disabled, loading }: React.ButtonHTMLAttributes<HTMLButtonElement> & ActionIconProps) {
    return (
        <ActionIcon
            className={classes.sendButton}
            data-testid="send-chat-message-button"
            id="send-chat-message-button"
            disabled={!!disabled}
            loading={loading}
            loaderProps={{ size: "sm", color: "gray.7" }}
            radius="xl"
            onClick={onClick}
            type="submit"
            size="xxl"
            variant="transparent"
        >
            <SendCircleIcon disabled={!!disabled} />
        </ActionIcon>
    );
}

interface MessageInputProps {
    chatTask: ChatTask & { customerName: { value?: string; error?: boolean } };
    scrollViewportRef: ScrollAreaViewport;
    chatId: string; // Required for component identity and more efficient updates
}

export function MessageInput({ chatTask, scrollViewportRef, chatId }: MessageInputProps) {
    const agentSdk = useAgentSdk();
    const globalNotification = useGlobalNotification();

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const inputs = useChatStore((state) => state.inputs);
    const setInput = useChatStore((state) => state.setInput);
    const input = inputs.get(chatId) ?? "";
    const [localInput, setLocalInput] = useState(input);

    const debouncedSetInput = useDebouncedCallback((value: string) => {
        setInput(chatId, value);
    }, 300);

    const containsPotentialPii = containsFourDigits(localInput);

    const submitMessage = async (message: string) => {
        const res = await agentSdk.sendMessage(chatTask, message);
        return res;
    };

    const handleSubmitMessage = () => {
        if (isSendDisabled) return;
        void submitMessageMutation(localInput);
    };

    const { isPending, mutateAsync: submitMessageMutation } = useMutation({
        retry: 2,
        mutationFn: submitMessage,
        onMutate: (message: string) => {
            logger.trace({ chatId: chatTask.chatId }, "Sending message");
            setLocalInput("");
            debouncedSetInput("");
            textareaRef.current?.focus();
            scrollToBottom(scrollViewportRef);
            return { message }; // allows us to access `context.message` in onError if needed
        },
        retryDelay(failureCount, error) {
            logger.warn({ chatId: chatTask.chatId, error }, `Retry sending message attempt: ${failureCount}`);
            return 1000 * 2 ** failureCount; // exponential backoff
        },
        onError: (error) => {
            globalNotification.error("Message failed to send. Please try again.");
            // Do not log message context here, as it may contain PII
            logger.error({ chatId: chatTask.chatId, error }, "Error sending message");
        },
    });

    const isSendDisabled = isPending || chatTask.status !== "assigned" || localInput.trimEnd() === "";
    /**
     * Input should be disabled if the task is not assigned.
     * ! Adding additional checks with this can break focus functionality if not accounted for.
     */
    const isInputDisabled = chatTask.status !== "assigned";

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputValue = event.currentTarget.value;
        setLocalInput(inputValue);
        debouncedSetInput(inputValue);
    };

    const handleBlur = useCallback(() => {
        debouncedSetInput.flush();
    }, [debouncedSetInput]);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault(); // Prevents Enter key from adding a new line
            void handleSubmitMessage();
        }
    };

    const placeholder = useMemo(() => {
        // strip newlines and trim whitespace from placeholder name to prevent height changes in the input
        const name = chatTask?.customerName?.value?.replace(/[\r\n]/g, " ").trim();
        // Use a 40-character threshold for customer names to ensure the placeholder remains concise
        return name && name.length <= 40 ? `Message ${name}` : "Message the customer";
    }, [chatTask?.customerName?.value]);

    return (
        <Textarea
            ref={textareaRef}
            id="chat-timeline-message-input"
            data-testid="chat-timeline-message-input"
            placeholder={placeholder}
            disabled={isInputDisabled}
            value={localInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            rightSection={<SendButton disabled={isSendDisabled} loading={isPending} onClick={handleSubmitMessage} />}
            className={containsPotentialPii ? "fs-mask" : undefined} // Hide input from full-story
            classNames={{ input: classes.input }}
            variant="filled"
            radius="xl"
            size="xl"
            w="100%"
            autosize
            minRows={1}
            maxRows={4}
            autoFocus
        />
    );
}
